import { useState, useEffect } from "react";
import { Calendar, Send, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const SERVICE_OPTIONS = [
  "Consulting",
  "Speaking",
  "Training",
  "AI",
  "Not sure yet",
];

const LOOKING_FOR_OPTIONS = ["Education", "Strategy", "Implementation", "All of the above"];
const TIMELINE_OPTIONS = ["ASAP", "1–3 months", "3–6 months", "Just exploring"];
const BUDGET_OPTIONS = [
  "Under $500",
  "$500 – $1,500",
  "$1,500 – $3,500",
  "$3,500 – $10,000",
  "$10,000+",
  "Not sure yet",
];

export default function BookCall() {
  const [calendlyUrl, setCalendlyUrl] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    website: "",
    service_interest: "",
    biggest_challenge: "",
    looking_for: "",
    timeline: "",
    budget: "",
    referral: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api.get("/config").then((r) => setCalendlyUrl(r.data.calendly_url)).catch(() => {});
  }, []);

  const handle = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/discovery-inquiry", form);
      setSubmitted(true);
      trackEvent("discovery_inquiry_submitted", {
        meta: { service: form.service_interest || "" },
      });
      toast.success("Inquiry sent! We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="relative bg-[var(--tmg-ivory)] border-b border-[var(--tmg-line)]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 pt-16 pb-16 sm:pt-24 sm:pb-20">
          <p className="tmg-overline mb-6 tmg-fade-up">— Book a Discovery Call</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] tracking-tight max-w-4xl tmg-fade-up-d1">
            Let's identify the right next step for your business.
          </h1>
          <p className="mt-8 text-base sm:text-lg text-[var(--tmg-muted)] max-w-2xl leading-relaxed tmg-fade-up-d2">
            For small business owners, entrepreneurs, and organizations who need help with
            AI education, workflow creation, app building, business systems, consulting, or
            funding readiness.
          </p>
        </div>
      </section>

      {/* Calendly Embed */}
      <section data-testid="calendly-section" className="tmg-section">
        <div className="tmg-container">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="sticky top-28">
                <p className="tmg-overline-gold mb-5">— Step 1</p>
                <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-tight mb-6">
                  Pick a time that works.
                </h2>
                <p className="text-[var(--tmg-muted)] leading-relaxed mb-6">
                  Choose a 30-minute discovery slot directly on our calendar. We'll
                  use the call to understand your business, your goals, and the right
                  starting point.
                </p>
                <div className="flex items-center gap-3 text-sm text-[var(--tmg-muted)] font-mono-sm">
                  <Calendar className="w-4 h-4 text-[var(--tmg-gold)]" />
                  30 minutes · Free consultation
                </div>
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="border border-[var(--tmg-line)] bg-white overflow-hidden" style={{ minHeight: 720 }}>
                {calendlyUrl ? (
                  <iframe
                    title="Calendly — Book a Discovery Call"
                    src={calendlyUrl}
                    className="w-full"
                    style={{ height: 720, border: "none" }}
                    data-testid="calendly-iframe"
                  />
                ) : (
                  <div className="p-10 text-center text-[var(--tmg-muted)]">Loading scheduler…</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry form */}
      <section className="tmg-section bg-[var(--tmg-ivory-2)]/50 border-y border-[var(--tmg-line)]">
        <div className="tmg-container">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <p className="tmg-overline-gold mb-5">— Step 2 (optional)</p>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold leading-tight mb-6">
                Share a bit about your business.
              </h2>
              <p className="text-[var(--tmg-muted)] leading-relaxed">
                Filling this out before our call helps us prepare a focused, valuable
                conversation tailored to where you are.
              </p>
            </div>

            <div className="lg:col-span-8">
              {submitted ? (
                <div data-testid="inquiry-success" className="bg-white border border-[var(--tmg-gold)] p-10 sm:p-14 text-center">
                  <CheckCircle2 className="w-14 h-14 text-[var(--tmg-gold)] mx-auto mb-6" />
                  <h3 className="font-display text-3xl font-semibold mb-3">Inquiry received.</h3>
                  <p className="text-[var(--tmg-muted)] max-w-md mx-auto">
                    Thank you! We'll review your inquiry and respond shortly. If you
                    haven't already, please book a time above.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  data-testid="inquiry-form"
                  className="bg-white border border-[var(--tmg-line)] p-8 sm:p-10 space-y-6"
                >
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field label="Full name *" required>
                      <Input data-testid="inquiry-name" required value={form.name} onChange={handle("name")} />
                    </Field>
                    <Field label="Email *" required>
                      <Input data-testid="inquiry-email" type="email" required value={form.email} onChange={handle("email")} />
                    </Field>
                    <Field label="Phone">
                      <Input data-testid="inquiry-phone" value={form.phone} onChange={handle("phone")} />
                    </Field>
                    <Field label="Business name">
                      <Input data-testid="inquiry-business" value={form.business_name} onChange={handle("business_name")} />
                    </Field>
                  </div>

                  <Field label="Website or social media link">
                    <Input data-testid="inquiry-website" placeholder="https://..." value={form.website} onChange={handle("website")} />
                  </Field>

                  <Field label="What service are you most interested in?">
                    <Select value={form.service_interest} onValueChange={handle("service_interest")}>
                      <SelectTrigger data-testid="inquiry-service"><SelectValue placeholder="Select a service" /></SelectTrigger>
                      <SelectContent>
                        {SERVICE_OPTIONS.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="What is your biggest challenge right now?">
                    <Textarea data-testid="inquiry-challenge" rows={3} value={form.biggest_challenge} onChange={handle("biggest_challenge")} />
                  </Field>

                  <div className="grid sm:grid-cols-3 gap-6">
                    <Field label="I'm looking for">
                      <Select value={form.looking_for} onValueChange={handle("looking_for")}>
                        <SelectTrigger data-testid="inquiry-looking"><SelectValue placeholder="Choose" /></SelectTrigger>
                        <SelectContent>
                          {LOOKING_FOR_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Timeline">
                      <Select value={form.timeline} onValueChange={handle("timeline")}>
                        <SelectTrigger data-testid="inquiry-timeline"><SelectValue placeholder="Choose" /></SelectTrigger>
                        <SelectContent>
                          {TIMELINE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Estimated budget">
                      <Select value={form.budget} onValueChange={handle("budget")}>
                        <SelectTrigger data-testid="inquiry-budget"><SelectValue placeholder="Choose" /></SelectTrigger>
                        <SelectContent>
                          {BUDGET_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <Field label="How did you hear about TMG Consulting Group?">
                    <Input data-testid="inquiry-referral" value={form.referral} onChange={handle("referral")} />
                  </Field>

                  <button
                    type="submit"
                    data-testid="inquiry-submit-btn"
                    disabled={submitting}
                    className="tmg-btn-primary w-full sm:w-auto disabled:opacity-60"
                  >
                    {submitting ? "Submitting…" : (<>Submit Inquiry <Send className="w-4 h-4" /></>)}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="tmg-overline">{label}</Label>
      {children}
    </div>
  );
}
